export interface JiraiQuestion {
  id: number;
  text: string;
  reversed: boolean;
  axis: 'dep' | 'emo' | 'att' | 'exp';
}

// 軸の意味:
// dep: 依存(A) / 回避(B)
// emo: 感情(E) / 理論(L)
// att: 攻撃(O) / 自傷(S)
// exp: 爆発(B) / 沈黙(Q)

export const jiraiQuestions: JiraiQuestion[] = [
  // 軸1: 依存/回避
  { id: 1,  text: '恋人や深い仲の相手から半日以上LINEの返信がないと、不安で何も手につかなくなる。',                                                                                reversed: false, axis: 'dep' },
  { id: 2,  text: '相手の予定や交友関係は、できるだけすべて把握しておきたい。',                                                                                                    reversed: false, axis: 'dep' },
  { id: 3,  text: '休日は一人の時間や自分の趣味を優先するより、少しでも長く相手と一緒にいたい。',                                                                                  reversed: false, axis: 'dep' },
  { id: 4,  text: '相手に少しでも「冷められた」と感じると、自分が傷つく前にこちらから関係を切り捨てたくなる。',                                                                    reversed: true,  axis: 'dep' },
  // 軸2: 感情/理論
  { id: 5,  text: '怒りや悲しみが限界に達すると、言葉をまとめるより先に涙が出たり感情が爆発してしまう。',                                                                          reversed: false, axis: 'emo' },
  { id: 6,  text: '相手を許すかどうかの基準は、「理屈として筋が通っているか」よりも「自分がどれだけ傷ついたか」だ。',                                                              reversed: false, axis: 'emo' },
  { id: 7,  text: '喧嘩の時はその場のノリや勢いでぶつかるより、LINEのスクショなどの「証拠」を並べて理詰めで追い詰める方が得意だ。',                                                  reversed: true,  axis: 'emo' },
  { id: 8,  text: '相手への怒りはその場で発散せず、一番ダメージを与えられるタイミングまで「冷凍保存」しておくことができる。',                                                        reversed: true,  axis: 'emo' },
  // 軸3: 攻撃/自傷
  { id: 9,  text: '喧嘩やトラブルが起きたとき、「どう考えても相手が悪い」「ふざけんな」と相手を責める感情が真っ先に湧く。',                                                          reversed: false, axis: 'att' },
  { id: 10, text: '相手に理不尽なことをされたら、相手の社会的地位やプライドを徹底的にへし折ってやりたいと思う。',                                                                    reversed: false, axis: 'att' },
  { id: 11, text: '限界を迎えた時は相手を直接責めるより、「私が全部悪いんだよね…」と自分を卑下して、相手に罪悪感を持たせたくなる。',                                                  reversed: true,  axis: 'att' },
  { id: 12, text: 'ムカつくことがあった時、SNSの裏垢には「あいつマジ許さん」という怒りよりも、「もう疲れた」「誰にも会いたくない」という病みポエムを書きがちだ。',                    reversed: true,  axis: 'att' },
  // 軸4: 爆発/沈黙
  { id: 13, text: '怒りが限界突破すると、大声を出したり、長文LINEを画面が埋まるほど連投してしまうことがある。',                                                                      reversed: false, axis: 'exp' },
  { id: 14, text: '関係がこじれた時は、朝までかかっても徹底的に話し合って（またはぶつかり合って）その日のうちに解決したい。',                                                          reversed: false, axis: 'exp' },
  { id: 15, text: '「もう無理」と思った相手には文句を言う気すらなくなり、ある日突然ブロックして無言で姿を消す。',                                                                      reversed: true,  axis: 'exp' },
  { id: 16, text: '相手への最大の復讐は、暴れて責め立てることではなく、「一切の反応を示さず、存在を完全に無視（透明化）すること」だと思う。',                                          reversed: true,  axis: 'exp' },
];

// 逆転項目のID
export const JIRAI_REVERSE_ITEMS = [4, 7, 8, 11, 12, 15, 16];

// ページ単位（4問ずつ）でシャッフルしたdisplayOrderを返す
export function getJiraiRandomDisplayOrder(): number[] {
  // 軸ごとに4問ずつのページを作る
  const pages: number[][] = [
    [1, 2, 3, 4],
    [5, 6, 7, 8],
    [9, 10, 11, 12],
    [13, 14, 15, 16],
  ];

  // ページ内をシャッフル
  pages.forEach((page) => {
    for (let i = page.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [page[i], page[j]] = [page[j], page[i]];
    }
  });

  // ページ順もシャッフル
  for (let i = pages.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pages[i], pages[j]] = [pages[j], pages[i]];
  }

  return pages.flat();
}

/**
 * 採点関数
 * answers: 長さ16の配列。answers[i] = 設問ID(i+1)への回答値（0〜6）
 * 左端(0) = そう思う = +3、右端(6) = そう思わない = -3
 * 逆転項目はスコアを反転
 * 各軸の合計が 0以上 → A側（依存D・感情E・攻撃O・爆発B）
 * 各軸の合計が -1以下 → B側（回避A・理論L・自傷S・沈黙Q）
 *
 * コード形式: [dep][emo][att][exp]
 * dep: D=依存, A=回避
 * emo: E=感情, L=理論
 * att: O=攻撃, S=自傷
 * exp: B=爆発, Q=沈黙
 */
export function calculateJiraiResult(answers: number[]): string {
  const scores = answers.map((answer, index) => {
    const raw = 3 - answer;
    const isReversed = JIRAI_REVERSE_ITEMS.includes(index + 1);
    return isReversed ? raw * -1 : raw;
  });

  const depSum = scores.slice(0, 4).reduce((a, b) => a + b, 0);
  const emoSum = scores.slice(4, 8).reduce((a, b) => a + b, 0);
  const attSum = scores.slice(8, 12).reduce((a, b) => a + b, 0);
  const expSum = scores.slice(12, 16).reduce((a, b) => a + b, 0);

  const dep = depSum >= 0 ? 'D' : 'A';
  const emo = emoSum >= 0 ? 'E' : 'L';
  const att = attSum >= 0 ? 'O' : 'S';
  const exp = expSum >= 0 ? 'B' : 'Q';

  return `${dep}${emo}${att}${exp}`;
}
